/**
 * @file services/guestStorage.js
 * @description LocalStorage-based data persistence for guest (unauthenticated) users.
 * Mirrors the Firebase service API so hooks can swap transparently.
 */

const KEYS = {
  meals: "guest_meals",
  profile: "guest_profile",
  weights: "guest_weights",
  workouts: "guest_workouts",
};

function read(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function write(key, data) {
  localStorage.setItem(key, JSON.stringify(data));
}

// ── MEALS ─────────────────────────────────────────────────────────────────────

export function getGuestMeals() {
  return read(KEYS.meals) || [];
}

export function addGuestMeal(mealData) {
  const meals = getGuestMeals();
  const newMeal = {
    ...mealData,
    id: `guest_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    createdAt: new Date().toISOString(),
  };
  meals.unshift(newMeal);
  write(KEYS.meals, meals);
  return newMeal;
}

export function deleteGuestMeal(mealId) {
  const meals = getGuestMeals().filter((m) => m.id !== mealId);
  write(KEYS.meals, meals);
}

export function updateGuestMeal(mealId, data) {
  const meals = getGuestMeals().map((m) =>
    m.id === mealId ? { ...m, ...data } : m,
  );
  write(KEYS.meals, meals);
}

// ── PROFILE ───────────────────────────────────────────────────────────────────

export function getGuestProfile() {
  return read(KEYS.profile) || null;
}

export function updateGuestProfile(data) {
  const existing = getGuestProfile() || {};
  write(KEYS.profile, { ...existing, ...data });
}

// ── WEIGHTS ───────────────────────────────────────────────────────────────────

export function getGuestWeights() {
  return read(KEYS.weights) || [];
}

export function addGuestWeight(entry) {
  const weights = getGuestWeights();
  weights.unshift({
    ...entry,
    id: `guest_${Date.now()}`,
    date: new Date().toISOString(),
  });
  write(KEYS.weights, weights);
}

// ── WORKOUTS ──────────────────────────────────────────────────────────────────

export function getGuestWorkouts() {
  return read(KEYS.workouts) || [];
}

export function addGuestWorkout(entry) {
  const workouts = getGuestWorkouts();
  workouts.push(entry);
  write(KEYS.workouts, workouts);
}

// ── MIGRATION ─────────────────────────────────────────────────────────────────

/**
 * Check if guest has any stored data worth migrating.
 */
export function hasGuestData() {
  return getGuestMeals().length > 0 || getGuestProfile() !== null;
}

/**
 * Clear all guest data after migration to Firebase.
 */
export function clearGuestData() {
  Object.values(KEYS).forEach((key) => localStorage.removeItem(key));
}
